import React, { useState, useCallback, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { useStoreState } from 'easy-peasy';
import { toast } from 'react-toastify';
import qs from 'querystringify';

import Layout from '../../../components/global/Layout';
import { useCompanyInvoiceSummary } from './hooks/useCompanyInvoiceSummary';
import { useSearchCompanyInvoice } from './hooks/useSearchCompanyInvoice';
import { useGenerateInvoice } from './hooks/useGenerateInvoice';
import { useUpdateInvoiceStatus } from './hooks/useUpdateInvoiceStatus';

import InvoiceStatCards from './components/InvoiceStatCards';
// import InvoiceCycleBanner from './components/InvoiceCycleBanner';
import InvoiceFilters from './components/InvoiceFilters';
import InvoiceTable from './components/InvoiceTable';
import InvoiceDrawer from './components/InvoiceDrawer';
import GenerateInvoiceModal from './components/GenerateInvoiceModal';
import UpdateStatusModal from './components/UpdateStatusModal';
import RunCycleModal from './components/RunCycleModal';
import DownloadModal from './components/DownloadModal';
import GenerationProgressModal from './components/GenerationProgressModal';

import styles from './components/PaymentInvoices.module.css';

const GENERATION_STEPS = [
  'Calculating sale_value for all orders...',
  'Applying KRAVEiN commission rates...',
  'Generating PDF invoices...',
  'Uploading to S3...',
  'Sending emails to venues...',
  'Triggering Stripe payouts...',
];

const PaymentInvoices = ({ history, location }) => {
  const { userId } = useStoreState(state => state.auth);

  const routeState = qs.parse(location.search);

  // ─── Summary stats (company_invoice_summary) ────────────────────────────────
  const {
    status: summaryStatus,
    totalInvoices,
    thisWeekInvoices,
    totalSaleValue,
    netPayout,
    applicationFee,
    kraveinFee,
    deliveryFee,
    kraveinCharges,
    creditCardFees,
    kraveinPayout,
    fromDate,
    toDate,
    pendingFromDate,
    pendingToDate,
    setDateRange,
    setWeekPreset,
    applyDateRange,
    refresh: refreshSummary,
  } = useCompanyInvoiceSummary(userId, {
    fromDate: routeState.fromDate,
    toDate: routeState.toDate,
  });

  // ─── Invoice table (search_company_invoice) ──────────────────────────────────
  const {
    status: searchStatus,
    invoices,
    totalSize,
    totalPages,
    page,
    pageSize,
    search,
    statusFilter,
    placeIdFilter,
    setSearch,
    setStatusFilter,
    setPlaceIdFilter,
    setPage,
    apply: applyFilters,
    refresh: refreshInvoices,
  } = useSearchCompanyInvoice(userId, fromDate, toDate, {
    search: routeState.search,
    status: routeState.status,
  });

  // ─── Status update (update_invoice_status) ──────────────────────────────────
  const {
    isUpdating,
    updateError,
    updateStatus,
    resetUpdateError,
  } = useUpdateInvoiceStatus(userId);

  // ─── Invoice generation (check_duplicate_invoice → generate_invoice) ─────────
  const {
    generateError,
    isGenerating,
    checkAndGenerate,
    resetGenerate,
  } = useGenerateInvoice(userId);

  // ─── Filter bar pending period ────────────────────────────────────────────────
  const [pendingPeriod, setPendingPeriod] = useState(
    routeState.period ?? 'this_week',
  );

  // ─── Drawer state ────────────────────────────────────────────────────────────
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // ─── Status modal state ───────────────────────────────────────────────────────
  const [statusModalInvoice, setStatusModalInvoice] = useState(null);

  // ─── Download modal state ─────────────────────────────────────────────────────
  const [downloadModalInvoice, setDownloadModalInvoice] = useState(null);

  const handleDownloadClick = useCallback(invoice => {
    setDownloadModalInvoice(invoice);
  }, []);

  // ─── Modal state ─────────────────────────────────────────────────────────────
  const [isGenModalOpen, setIsGenModalOpen] = useState(false);
  const [isRunModalOpen, setIsRunModalOpen] = useState(false);
  const [isProgressOpen, setIsProgressOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressStep, setProgressStep] = useState('');
  const [isGenerationDone, setIsGenerationDone] = useState(false);
  const progressInterval = useRef(null);

  // ─── Status modal handlers ────────────────────────────────────────────────────
  const handleStatusClick = useCallback(
    invoice => {
      resetUpdateError();
      setStatusModalInvoice(invoice);
    },
    [resetUpdateError],
  );

  const handleStatusConfirm = useCallback(
    async (companyInvoiceId, status) => {
      const result = await updateStatus(companyInvoiceId, status);
      if (result) {
        setStatusModalInvoice(null);
        refreshInvoices();
      }
    },
    [updateStatus, refreshInvoices],
  );

  const handleStatusModalClose = useCallback(() => {
    resetUpdateError();
    setStatusModalInvoice(null);
  }, [resetUpdateError]);

  // ─── Drawer handlers ─────────────────────────────────────────────────────────
  const openDrawer = useCallback(invoice => {
    setSelectedInvoice(invoice);
    setIsDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedInvoice(null);
  }, []);

  // ─── Generation progress simulation ──────────────────────────────────────────
  const startProgress = useCallback(() => {
    setProgress(0);
    setProgressStep(GENERATION_STEPS[0]);
    setIsGenerationDone(false);
    setIsProgressOpen(true);

    let pct = 0;
    let stepIndex = 0;
    clearInterval(progressInterval.current);
    progressInterval.current = setInterval(() => {
      pct = Math.min(pct + Math.floor(Math.random() * 9) + 4, 100);
      setProgress(pct);
      if (pct >= stepIndex * 16 && stepIndex < GENERATION_STEPS.length) {
        setProgressStep(GENERATION_STEPS[stepIndex]);
        stepIndex += 1;
      }
      if (pct >= 100) {
        clearInterval(progressInterval.current);
        setIsGenerationDone(true);
      }
    }, 200);
  }, []);

  // ─── Generate invoice flow ───────────────────────────────────────────────────
  const handleGenerate = useCallback(
    async ({
      companies,
      fromDate: invoiceFrom,
      toDate: invoiceTo,
      autoGeneratePdf,
      autoSendEmail,
      requireApproval,
      isKraveinFeeInclude,
      paymentDate,
      isCashInclude,
      allCompanies,
    }) => {
      const result = await checkAndGenerate({
        companies,
        fromDate: invoiceFrom,
        toDate: invoiceTo,
        autoGeneratePdf,
        autoSendEmail,
        requireApproval,
        isKraveinFeeInclude,
        paymentDate,
        isCashInclude,
        allCompanies,
      });
      if (result) {
        setIsGenModalOpen(false);
        resetGenerate();
        startProgress();
      }
      // on error: modal stays open, generateError is surfaced inside it
    },
    [checkAndGenerate, resetGenerate, startProgress],
  );

  const handleConfirmRun = useCallback(() => {
    setIsRunModalOpen(false);
    startProgress();
  }, [startProgress]);

  const handleProgressDone = useCallback(() => {
    setIsProgressOpen(false);
    resetGenerate();
    refreshSummary();
    refreshInvoices();
  }, [resetGenerate, refreshSummary, refreshInvoices]);

  const handlePreview = useCallback(() => {
    setIsGenModalOpen(false);
    // TODO: dry-run preview logic
  }, []);

  return (
    <Layout>
      <div className={styles.page}>
        {/* ─── Topbar ─── */}
        <div className={styles.topbar}>
          <div className={styles.topbarLeft}>
            <span className={styles.pageTitle}>Invoice Management</span>
            {/* <span className={styles.liveDot} />
            <span className={styles.liveLabel}>Live</span> */}
          </div>
          <div className={styles.topbarRight}>
            {/* <button
              className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
              onClick={() => setIsRunModalOpen(true)}
            >
              <span role="img" aria-label="lightning">
                ⚡
              </span>{' '}
              Run Invoice Cycle
            </button> */}
            <button
              className={`${styles.btn} ${styles.btnTeal}`}
              onClick={() => setIsGenModalOpen(true)}
            >
              <span role="img" aria-label="invoice">
                🧾
              </span>{' '}
              Generate Invoices
            </button>
          </div>
        </div>

        {/* ─── Content ─── */}
        <div className={styles.content}>
          <InvoiceStatCards
            status={summaryStatus}
            totalInvoices={totalInvoices}
            thisWeekInvoices={thisWeekInvoices}
            totalSaleValue={totalSaleValue}
            netPayout={netPayout}
            applicationFee={applicationFee}
            kraveinFee={kraveinFee}
            deliveryFee={deliveryFee}
            kraveinCharges={kraveinCharges}
            creditCardFees={creditCardFees}
            kraveinPayout={kraveinPayout}
          />

          {/* <InvoiceCycleBanner
            fromDate={fromDate}
            toDate={toDate}
            netPayoutQueued={netPayout}
            onRunThisWeek={() => setIsRunModalOpen(true)}
            onDownloadReport={() => {}}
          /> */}

          <InvoiceFilters
            search={search}
            status={statusFilter}
            placeIdFilter={placeIdFilter}
            setPlaceIdFilter={setPlaceIdFilter}
            fromDate={pendingFromDate}
            toDate={pendingToDate}
            period={pendingPeriod}
            onSearchChange={setSearch}
            onStatusChange={setStatusFilter}
            onFromChange={d => setDateRange(d, pendingToDate)}
            onToChange={d => setDateRange(pendingFromDate, d)}
            onRangeChange={setDateRange}
            onWeekPreset={setWeekPreset}
            onPeriodChange={setPendingPeriod}
            onApply={() => {
              applyDateRange();
              applyFilters(pendingFromDate, pendingToDate);
              history.push(
                `${location.pathname}${qs.stringify(
                  {
                    search: search || undefined,
                    status: statusFilter || undefined,
                    fromDate: pendingFromDate || undefined,
                    toDate: pendingToDate || undefined,
                    period: pendingPeriod || undefined,
                  },
                  true,
                )}`,
              );
            }}
            onExport={() => {}}
          />

          <InvoiceTable
            status={searchStatus}
            invoices={invoices}
            totalSize={totalSize}
            page={page}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={setPage}
            onRowClick={openDrawer}
            onStatusClick={handleStatusClick}
            onViewFull={inv =>
              history.push(`/payment-invoice/${inv.companyInvoiceId}`)
            }
            onDownloadClick={handleDownloadClick}
            onSendSelected={async ids => {
              const result = await updateStatus(ids, 'EMAILSENT');
              if (result) refreshInvoices();
              return result;
            }}
            isSendingStatus={isUpdating}
            sendStatusError={updateError}
            onMarkAsPaid={async ids => {
              const result = await updateStatus(ids, 'PAID');
              if (result) refreshInvoices();
              return result;
            }}
            isMarkingPaid={isUpdating}
            markPaidError={updateError}
            onRegenerate={() => {}}
          />
        </div>

        <InvoiceDrawer
          invoice={selectedInvoice}
          isOpen={isDrawerOpen}
          onClose={closeDrawer}
          onViewFull={() => {
            if (selectedInvoice) {
              history.push(
                `/payment-invoice/${selectedInvoice.companyInvoiceId}`,
              );
            }
          }}
          onPdfDownload={inv => {
            if (inv.pdfUrl) {
              window.open(inv.pdfUrl, '_blank');
            } else {
              toast.error('This invoice does not have a downloadable PDF link');
            }
          }}
          onCsvDownload={inv => {
            if (inv.csvUrl) {
              window.open(inv.csvUrl, '_blank');
            } else {
              toast.error('This invoice does not have a downloadable CSV link');
            }
          }}
          onResend={() => {}}
        />

        {isGenModalOpen && (
          <GenerateInvoiceModal
            isOpen={isGenModalOpen}
            onClose={() => {
              setIsGenModalOpen(false);
              resetGenerate();
            }}
            onPreview={handlePreview}
            onGenerate={handleGenerate}
            generateError={generateError}
            isLoading={isGenerating}
          />
        )}

        <UpdateStatusModal
          isOpen={!!statusModalInvoice}
          invoice={statusModalInvoice}
          isUpdating={isUpdating}
          updateError={updateError}
          onConfirm={handleStatusConfirm}
          onClose={handleStatusModalClose}
        />

        <RunCycleModal
          isOpen={isRunModalOpen}
          fromDate={fromDate}
          toDate={toDate}
          invoiceCount={thisWeekInvoices}
          netPayout={netPayout}
          onClose={() => setIsRunModalOpen(false)}
          onConfirm={handleConfirmRun}
        />

        <DownloadModal
          isOpen={!!downloadModalInvoice}
          invoice={downloadModalInvoice}
          onClose={() => setDownloadModalInvoice(null)}
        />

        <GenerationProgressModal
          isOpen={isProgressOpen}
          progress={progress}
          step={progressStep}
          isDone={isGenerationDone}
          onDone={handleProgressDone}
        />
      </div>
    </Layout>
  );
};

export default withRouter(PaymentInvoices);
